// Drawer → Settings (acceptance O1–O5 + Phase C UI-6 "Keep warm"): the idle
// timeout, the keep-warm switch for voice, and the vLLM serving settings.
import { useState } from "react";
import { modelServingApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import { IDLE_CHOICES, gpuText, modelStatus, settingsChanges, settingsForm } from "./modelServingModel";
import { Btn, Field, MONO, Notice, errorText, input } from "./modelServingUi";

export default function ModelDrawerSettings({ model, preset, serving, onChanged }) {
  const [form, setForm] = useState(() => settingsForm(model));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);           // {tone, text}
  const keepWarm = !!(serving?.keep_warm ?? model.endpoint?.keep_warm);
  const running = ["running", "warming"].includes(modelStatus(model));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (restart) => {
    const changes = settingsChanges(model, form);
    if (!Object.keys(changes).length) {
      setMsg({ tone: "info", text: "Nothing changed." });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      await modelServingApi.saveSettings(model.key, changes, restart);
      setMsg({ tone: "ok", text: restart ? "Saved. The model is restarting with the new settings."
        : running ? "Saved. Serving settings apply at the next warm-up." : "Saved." });
      onChanged?.();
    } catch (e) {
      setMsg({ tone: "bad", text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const toggleKeepWarm = async () => {
    setBusy(true); setMsg(null);
    try {
      await modelServingApi.keepWarm(model.key, !keepWarm);
      onChanged?.();
    } catch (e) {
      setMsg({ tone: "bad", text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Field label="Keep warm"
        hint={model.endpoint
          ? "For voice and other latency-sensitive use: the model never parks on its own, so no request waits for a cold start. It keeps costing its GPU price until you turn this off or press Stop."
          : "Warm the model up once to turn this on — that's when its endpoint is created."}>
        <button type="button" role="switch" aria-checked={keepWarm} data-lc="keep-warm" disabled={busy || !model.endpoint}
          onClick={toggleKeepWarm}
          style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "6px 12px 6px 6px", borderRadius: 999,
            cursor: "pointer", border: `1px solid ${keepWarm ? COLORS.green : COLORS.border}`,
            background: keepWarm ? `${COLORS.green}18` : "transparent", color: COLORS.text, fontSize: 13, fontWeight: 600 }}>
          <span style={{ width: 30, height: 18, borderRadius: 999, position: "relative",
            background: keepWarm ? COLORS.green : COLORS.border }}>
            <span style={{ position: "absolute", top: 2, left: keepWarm ? 14 : 2, width: 14, height: 14, borderRadius: "50%",
              background: COLORS.surface }} />
          </span>
          {keepWarm ? "Kept warm — never parks on its own" : "Off — parks when idle"}
        </button>
      </Field>
      <Field label="Park after no requests for" htmlFor="ms-idle"
        hint={keepWarm ? "Not used while the model is kept warm." : "Shorter saves money; longer avoids cold starts for bursty traffic."}>
        <select id="ms-idle" data-lc="idle-timeout" style={input()} value={form.idle_timeout_minutes}
          onChange={set("idle_timeout_minutes")}>
          {IDLE_CHOICES.map((c) => <option key={c.value} value={String(c.value)}>{c.label}</option>)}
        </select>
      </Field>
      <Field label="GPU" hint="You choose the GPU each time you warm the model up.">
        <div style={{ fontWeight: 600, fontSize: 13 }}>{gpuText(model, preset)}</div>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
        <Field label="Max context length" htmlFor="ms-s-ctx">
          <input id="ms-s-ctx" data-lc="serve-settings" style={input()} inputMode="numeric" placeholder="model default"
            value={form.max_model_len} onChange={set("max_model_len")} />
        </Field>
        <Field label="Max parallel requests" htmlFor="ms-s-seqs">
          <input id="ms-s-seqs" style={input()} inputMode="numeric" placeholder="vLLM default"
            value={form.max_num_seqs} onChange={set("max_num_seqs")} />
        </Field>
      </div>
      <Field label="vLLM image" htmlFor="ms-s-image">
        <input id="ms-s-image" style={input()} value={form.image} onChange={set("image")} />
      </Field>
      <Field label="Extra vLLM arguments" htmlFor="ms-s-args">
        <input id="ms-s-args" style={{ ...input(), fontFamily: MONO }} value={form.extra_args} onChange={set("extra_args")} />
      </Field>
      {running && (
        <Notice tone="warn" icon="↻">
          <b style={{ color: COLORS.text }}>The model is running.</b> Serving settings apply at the next warm-up. The idle
          timeout applies immediately.
        </Notice>
      )}
      {msg && <Notice testid="settings-msg" tone={msg.tone} icon={msg.tone === "bad" ? "!" : "✓"}>{msg.text}</Notice>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Btn disabled={busy} onClick={() => save(false)}>Save · apply at next warm-up</Btn>
        <Btn kind="primary" disabled={busy || !running} onClick={() => save(true)}>Save &amp; restart now</Btn>
      </div>
    </div>
  );
}
